/**
 * Share design links.
 *
 * A shared design is nothing but the current design state encoded into the
 * product URL: ?design=<base64url json>&variant=<id>. Opening such a link
 * re-signs the stored upload through the existing /api/media/preview endpoint
 * and replays the state onto the canvas, so no new storage is required.
 *
 * Payload keys are kept short so the link stays reasonably sized:
 *   p  original storage path      b   background color
 *   m  mime type                  ox  x offset, as a fraction of canvas width
 *   s  scale percentage           oy  y offset, as a fraction of canvas height
 *   r  rotation in degrees        rb  background removed (1/0)
 */
(function () {
  const API_BASE_URL = 'https://api.rugtomize.co';
  const PARAM = 'design';
  const READY_TIMEOUT = 20000;

  /* ---- ENCODING ---- */

  function encodePayload(payload) {
    const json = JSON.stringify(payload);
    return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePayload(raw) {
    try {
      const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(decodeURIComponent(escape(atob(base64))));
    } catch (error) {
      console.error('Invalid design link', error);
      return null;
    }
  }

  /* ---- HELPERS ---- */

  function editor() {
    return window.CanvasRugEditor || null;
  }

  function canvasSize() {
    const canvas = document.querySelector('#rug-canvas');
    return { width: canvas?.clientWidth || 0, height: canvas?.clientHeight || 0 };
  }

  function waitFor(check, timeout = READY_TIMEOUT) {
    return new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        const value = check();
        if (value) return resolve(value);
        if (Date.now() - started > timeout) return resolve(null);
        setTimeout(tick, 150);
      };
      tick();
    });
  }

  function preloadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = src;
    });
  }

  /* ---- SHARING ---- */

  function buildShareUrl() {
    const rug = editor();
    const path = rug?.uploadedImage?.path;
    if (!path) return null;

    const { width, height } = canvasSize();
    const payload = {
      p: path,
      m: rug.uploadedImage.mimeType || '',
      s: Math.round(rug.scalePct),
      r: Math.round(rug.rotationDeg),
      b: rug.bgColor || '#000000',
      ox: width ? +(rug.offset.x / width).toFixed(4) : 0,
      oy: height ? +(rug.offset.y / height).toFixed(4) : 0,
      rb: rug.removedBackground && rug.shownImage === 'background-removed' ? 1 : 0
    };

    const url = new URL(window.location.origin + window.location.pathname);
    const variantId =
      new URLSearchParams(window.location.search).get('variant') ||
      document.querySelector('#product-form-custom [name="id"]')?.value;

    if (variantId) url.searchParams.set('variant', variantId);
    url.searchParams.set(PARAM, encodePayload(payload));

    return url.toString();
  }

  async function onShareClick(event) {
    const button = event.currentTarget;
    if (button.dataset.busy === 'true') return;

    button.dataset.busy = 'true';

    try {
      // The upload finishes in the background, so the path may not be there yet.
      if (!editor()?.uploadedImage?.path) {
        window.GlobalLoader?.show('Preparing your design link...');
        await waitFor(() => editor()?.uploadedImage?.path);
        window.GlobalLoader?.hide();
      }

      const url = buildShareUrl();
      if (!url) {
        alert('Your design is still uploading. Please try again in a moment.');
        return;
      }

      if (navigator.share) {
        await navigator.share({ title: document.title, url: url });
        theme.utils.track('Share Design', { method: 'native' });
        return;
      }

      await navigator.clipboard.writeText(url);
      flashCopied(button);
      theme.utils.track('Share Design', { method: 'clipboard' });
    } catch (error) {
      // A dismissed native share sheet rejects too, which is not an error.
      if (error?.name !== 'AbortError') console.error(error);
      window.GlobalLoader?.hide();
    } finally {
      button.dataset.busy = 'false';
    }
  }

  function flashCopied(button) {
    const label = button.querySelector('[data-share-label]');
    if (!label) return;
    const original = label.textContent;
    label.textContent = 'Link copied!';
    button.dataset.copied = 'true';
    setTimeout(() => {
      label.textContent = original;
      button.dataset.copied = 'false';
    }, 2500);
  }

  /* ---- RESTORING ---- */

  async function restore(payload) {
    const rug = await waitFor(editor);
    if (!rug || !payload.p) return;

    window.GlobalLoader?.show(['Loading the shared design...', 'Almost ready...']);
    theme.utils.track('Shared Design Opened');

    try {
      const res = await fetch(`${API_BASE_URL}/api/media/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: payload.p })
      });
      if (!res.ok) throw new Error('Failed to load shared design: ' + res.status);

      const { url } = await res.json();
      const imageRes = await fetch(url);
      if (!imageRes.ok) throw new Error('Failed to download shared image');
      const blob = await imageRes.blob();
      const objectUrl = URL.createObjectURL(blob);

      await preloadImage(objectUrl);

      const { width, height } = canvasSize();

      rug.reset();
      rug.offset = { x: (payload.ox || 0) * width, y: (payload.oy || 0) * height };
      rug.scalePct = Number(payload.s) || 100;
      rug.rotationDeg = Number(payload.r) || 0;
      rug.setBgColor(payload.b || '#000000');
      rug.loadForeground(objectUrl);
      rug.setUploadedImage({
        path: payload.p,
        mimeType: payload.m || '',
        originalPreviewFileUrl: url,
        previewDataUrl: blob
      });

      if (payload.rb) rug.startRemoveBg();

      theme.utils.track('Shared Design Loaded', { removed_background: payload.rb ? 'yes' : 'no' });
    } catch (error) {
      console.error(error);
      theme.utils.track('Shared Design Failed');
    } finally {
      window.GlobalLoader?.hide();
    }
  }

  /* ---- INIT ---- */

  function init() {
    document.querySelector('#custom-rug-share-design')?.addEventListener('click', onShareClick);

    const raw = new URLSearchParams(window.location.search).get(PARAM);
    if (!raw) return;

    const payload = decodePayload(raw);
    if (payload) restore(payload);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
